import type {
  LeadScore,
  LeadScoreResponse,
  LeadScoreStats
} from "../../../types"
import type { LeadScoreCategory } from "../../../types"
import type { AcknowledgedFilter } from "./category-filter-bar"

/**
 * Local UI testing only. Keep `false` in committed code; set `true` locally.
 */
export const USE_LEAD_SCORING_MOCK = false

const MOCK_LO: LeadScore["loan_officer"] = {
  loid: 1,
  name: "Alex Morgan",
  email: "alex@example.com",
  phone: "5550123456",
  nmls: "123456",
  photo: null
}

const MOCK_PAGE_SIZE = 50

const ALL_MOCK_LEADS: LeadScore[] = [
  {
    id: "mock-1",
    subscription_id: "sub-1",
    intent_score: 87,
    category: "hot_lead",
    summary:
      "Highly engaged FHA purchase lead with strong buying signals. Completed full workflow and is monitoring rate quotes.",
    reasoning:
      "Multiple dashboard visits, repeated rate checks, and fast responses to SMS indicate high intent.",
    suggested_action:
      "Call Jimmy today about VA options and 15-year terms he mentioned.",
    key_signals: [
      "Ready to lock 5.375% VA rate",
      "Active negotiations on $1.3M purchase",
      "Multiple requests for LO guidance"
    ],
    model: "gpt-4",
    scored_at: Math.floor(Date.now() / 1000) - 3600,
    acknowledged_at: null,
    loan_officer: MOCK_LO,
    subscription: {
      first_name: "Jimmy",
      last_name: "King",
      email: "jimmy.k@example.com",
      phone: "5550123456",
      source: "web",
      active: 1,
      created: Math.floor(Date.now() / 1000) - 86400 * 3,
      quote_info: {
        loan_purpose: "Purchase",
        loan_amount: 425_000,
        loan_type: "VA",
        loan_term: 30,
        location: { city: "Miami", state: "FL", zipcode: "33101" }
      }
    }
  },
  {
    id: "mock-2",
    subscription_id: "sub-2",
    intent_score: 72,
    category: "needs_contact",
    summary:
      "Qualified borrower paused after initial quote; nudge recommended.",
    reasoning: "Opened email twice but did not return to the portal.",
    suggested_action: "Send a short follow-up offering a quick call this week.",
    key_signals: ["Opened pricing email", "No login in 5 days"],
    model: "gpt-4",
    scored_at: Math.floor(Date.now() / 1000) - 7200,
    acknowledged_at: null,
    loan_officer: MOCK_LO,
    subscription: {
      first_name: "Sam",
      last_name: "Rivera",
      email: "sam.r@example.com",
      phone: "5550123456",
      source: "web",
      active: 1,
      created: Math.floor(Date.now() / 1000) - 86400,
      quote_info: {
        loan_purpose: "Purchase",
        loan_amount: 310_000,
        loan_type: "Conventional",
        loan_term: 30,
        location: { city: "Austin", state: "TX" }
      }
    }
  },
  {
    id: "mock-3",
    subscription_id: "sub-3",
    intent_score: 58,
    category: "engaged",
    summary: "Steady engagement; browsing programs.",
    reasoning: "Regular visits but no lock request yet.",
    suggested_action: "Share a comparison sheet for 30 vs 15 year payments.",
    key_signals: ["3 sessions this week"],
    model: "gpt-4",
    scored_at: Math.floor(Date.now() / 1000) - 86400,
    acknowledged_at: Math.floor(Date.now() / 1000) - 1000,
    loan_officer: MOCK_LO,
    subscription: {
      first_name: "Taylor",
      last_name: "Chen",
      email: "taylor.c@example.com",
      phone: "5550123456",
      source: "web",
      active: 1,
      created: Math.floor(Date.now() / 1000) - 86400 * 5,
      quote_info: {
        loan_amount: 510_000,
        loan_type: "FHA",
        loan_term: 30,
        location: { city: "Seattle", state: "WA" }
      }
    }
  },
  {
    id: "mock-4",
    subscription_id: "sub-4",
    intent_score: 41,
    category: "at_risk",
    summary: "Cooling activity; shorter sessions.",
    reasoning: "Declining return rate compared to prior weeks.",
    suggested_action:
      "Proactive check-in; emphasize service and speed to close.",
    key_signals: ["Session length down 40%"],
    model: "gpt-4",
    scored_at: Math.floor(Date.now() / 1000) - 172800,
    acknowledged_at: null,
    loan_officer: MOCK_LO,
    subscription: {
      first_name: "Jordan",
      last_name: "Lee",
      email: "jordan.l@example.com",
      phone: "5550123456",
      source: "web",
      active: 1,
      created: Math.floor(Date.now() / 1000) - 86400 * 10,
      quote_info: {
        loan_purpose: "Purchase",
        loan_amount: 275_000,
        loan_type: "Conventional",
        loan_term: 30,
        location: { city: "Denver", state: "CO" }
      }
    }
  }
]

function computeStats(leads: LeadScore[]): LeadScoreStats {
  const n = leads.length
  const sum = leads.reduce((a, s) => a + Number(s.intent_score), 0)
  const avg = n ? Math.round((sum / n) * 10) / 10 : 0
  const unacknowledged = leads.filter(s => s.acknowledged_at == null).length

  return {
    total: n,
    hot_lead: leads.filter(s => s.category === "hot_lead").length,
    needs_contact: leads.filter(s => s.category === "needs_contact").length,
    engaged: leads.filter(s => s.category === "engaged").length,
    dormant: leads.filter(s => s.category === "dormant").length,
    at_risk: leads.filter(s => s.category === "at_risk").length,
    avg_score: avg,
    unacknowledged
  }
}

function filterLeads(
  leads: LeadScore[],
  category: LeadScoreCategory | null,
  acknowledgedFilter: AcknowledgedFilter
): LeadScore[] {
  let out = leads
  if (category) {
    out = out.filter(s => s.category === category)
  }
  if (acknowledgedFilter === "new") {
    out = out.filter(s => s.acknowledged_at == null)
  } else if (acknowledgedFilter === "reviewed") {
    out = out.filter(s => s.acknowledged_at != null)
  }
  return out
}

export function getMockLeadScoringListResponse(options: {
  activeCategory: LeadScoreCategory | null
  acknowledgedFilter: AcknowledgedFilter
  page: number
}): LeadScoreResponse {
  const filtered = filterLeads(
    ALL_MOCK_LEADS,
    options.activeCategory,
    options.acknowledgedFilter
  )
  const total = filtered.length
  const total_pages = Math.max(1, Math.ceil(total / MOCK_PAGE_SIZE))
  const page = Math.min(Math.max(1, options.page), total_pages)
  const offset = (page - 1) * MOCK_PAGE_SIZE
  const slice = filtered.slice(offset, offset + MOCK_PAGE_SIZE)

  return {
    data: slice,
    stats: computeStats(ALL_MOCK_LEADS),
    pagination: {
      total,
      limit: MOCK_PAGE_SIZE,
      offset,
      page,
      total_pages
    }
  }
}

export function getMockLeadScoringStatsResponse(): LeadScoreResponse {
  return {
    data: [],
    stats: computeStats(ALL_MOCK_LEADS),
    pagination: {
      total: 0,
      limit: 1,
      offset: 0,
      page: 1,
      total_pages: 1
    }
  }
}
